import React, { useState } from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCard from "../../system-design/DSCard";
import DSAvatar from "../../system-design/DSAvatar";
import DSTooltip from "../../system-design/DSTooltip";
import DSChip from "../../system-design/DSChip";
import KlydoLoopUrl from "../../../components/klydoPreview/KlydoLoopUrl";
import LoadKlydosButton from "./LoadKlydosButton";
import useLoadKlydos from "../hooks/useLoadKlydos";
import { KLYDO_BLACK } from "../../../consts/colors";
import MultiCalendarEventModal from "./MultiCalendarEventModal";
import KlydoEventDayModal from "./KlydoEventDayModal";
import UnifiedCalendarEventModal from "./UnifiedCalendarEventModal";
import KlydoModal from "../../Klydos/components/KlydoModal";
import DSDialog from "../../system-design/DSDialog";
import { Klydo } from "../../../api/klydo";
import {
  DateTime,
  KLYDO_EVENT_PRIMARY_TYPE,
  PopulatedCalendarEvent,
} from "../../../api/calendarEvent";

type CalendarEventDayProps = {
  date: DateTime;
  event: PopulatedCalendarEvent[];
  country: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  hasCountry: boolean;
};

const CalendarEventDay = ({
  date,
  event,
  country,
  isCurrentMonth,
  isToday,
  hasCountry,
}: CalendarEventDayProps) => {
  const [showCalendarEventModal, setShowCalendarEventModal] = useState(false);
  const [showKlydoEventDayModal, setShowKlydoEventDayModal] = useState(false);
  const [showCreateKlydoEvent, setShowCreateKlydoEvent] = useState(false);
  const [selectedKlydo, setSelectedKlydo] = useState<Klydo | null>(null);

  const isKlydoEvent = (calendarEvent: PopulatedCalendarEvent) =>
    calendarEvent.primaryType === KLYDO_EVENT_PRIMARY_TYPE ||
    (calendarEvent.klydoIds?.length ?? 0) > 0;

  const holidayEvents = event.filter(
    (calendarEvent) => !isKlydoEvent(calendarEvent),
  );
  const klydoEvents = event.filter((calendarEvent) =>
    isKlydoEvent(calendarEvent),
  );

  const hasHolidayEvents = holidayEvents.length > 0;
  const hasKlydoEvents = klydoEvents.length > 0;

  const {
    populatedEvents,
    klydosLoading,
    klydosError,
    handleLoadKlydos,
    klydosLoaded,
  } = useLoadKlydos({
    year: date.year,
    month: date.month,
    day: date.day,
    country,
    enabled: !!country && hasHolidayEvents,
  });

  const displayedHolidayEvents = populatedEvents || holidayEvents;
  const allKlydos = displayedHolidayEvents.flatMap(
    (event) => event.klydos || [],
  );

  const handleDayClick = () => {
    if (hasHolidayEvents && hasKlydoEvents) {
      setShowCalendarEventModal(true);
    } else if (hasKlydoEvents) {
      setShowKlydoEventDayModal(true);
    } else if (hasHolidayEvents) {
      setShowCalendarEventModal(true);
    } else {
      setShowCreateKlydoEvent(true);
    }
  };

  const handleMoreKlydosClick = (mouseEvent: React.MouseEvent) => {
    mouseEvent.stopPropagation();
    if (hasHolidayEvents) {
      setShowCalendarEventModal(true);
    }
  };

  const dateAsJsDate = new Date(date.year, date.month - 1, date.day);

  return (
    <>
      <DSCard
        onClick={handleDayClick}
        sx={{
          minHeight: 120,
          p: 1,
          border: isToday ? "2px solid #1976d2" : "1px solid #e0e0e0",
          backgroundColor: isCurrentMonth ? "background.paper" : "action.hover",
          opacity: isCurrentMonth ? 1 : 0.6,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <DSBox
          sx={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <DSTypography
            variant="body2"
            sx={{
              fontWeight: isToday ? "bold" : "normal",
              color: isToday ? "primary.main" : "text.primary",
              mb: 1,
            }}
          >
            {date.day}
          </DSTypography>

          {hasHolidayEvents && (
            <DSBox sx={{ mb: 0.5 }}>
              {hasCountry ? (
                holidayEvents.map((evt) => (
                  <DSTooltip key={evt.id} title={evt.name}>
                    <DSChip
                      label={evt.name}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.6rem",
                        height: "auto",
                        mb: 0.5,
                        mr: 0.5,
                        borderColor: KLYDO_BLACK,
                        color: KLYDO_BLACK,
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        },
                      }}
                    />
                  </DSTooltip>
                ))
              ) : (
                <DSChip
                  label={`${holidayEvents.length} holiday${holidayEvents.length > 1 ? "s" : ""}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.6rem",
                    height: "auto",
                    mb: 0.5,
                    borderColor: KLYDO_BLACK,
                    color: KLYDO_BLACK,
                  }}
                />
              )}
            </DSBox>
          )}

          {hasKlydoEvents && (
            <DSBox sx={{ mb: 0.5 }}>
              {klydoEvents.map((evt) => (
                <DSTooltip
                  key={evt.id}
                  title={`Klydo Event: ${evt.name} (${evt.klydoIds?.length || 0} klydos)`}
                >
                  <DSChip
                    label={`K: ${evt.name}`}
                    size="small"
                    variant="filled"
                    sx={{
                      fontSize: "0.6rem",
                      height: "auto",
                      mb: 0.5,
                      mr: 0.5,
                      backgroundColor: "secondary.main",
                      color: "common.white",
                      "& .MuiChip-label": {
                        display: "block",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                      },
                    }}
                  />
                </DSTooltip>
              ))}
              <DSBox
                sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}
              >
                {klydoEvents
                  .flatMap((evt) => evt.klydos || [])
                  .slice(0, 3)
                  .map((klydo) => (
                    <DSTooltip
                      key={klydo.id}
                      title={`${klydo.name} by ${klydo.author?.name || klydo.creator}`}
                    >
                      <DSBox
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setSelectedKlydo(klydo);
                        }}
                        sx={{
                          width: 20,
                          height: 20,
                          cursor: "pointer",
                          overflow: "hidden",
                          borderRadius: "50%",
                          "&:hover": { transform: "scale(1.1)" },
                        }}
                      >
                        {klydo.loopUrl ? (
                          <KlydoLoopUrl loopUrl={klydo.loopUrl} />
                        ) : (
                          <DSAvatar
                            src={klydo.klydoSecureUrl || undefined}
                            sx={{ width: "100%", height: "100%" }}
                          >
                            {klydo.name.charAt(0)}
                          </DSAvatar>
                        )}
                      </DSBox>
                    </DSTooltip>
                  ))}
              </DSBox>
            </DSBox>
          )}

          {hasHolidayEvents && (
            <DSBox sx={{ flex: 1 }}>
              {allKlydos.length > 0 && (
                <DSBox
                  sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}
                >
                  {allKlydos.slice(0, 3).map((klydo) => (
                    <DSTooltip
                      key={klydo.id}
                      title={`${klydo.name} by ${klydo.author?.name || klydo.creator}`}
                    >
                      <DSBox
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setSelectedKlydo(klydo);
                        }}
                        sx={{
                          width: 20,
                          height: 20,
                          cursor: "pointer",
                          overflow: "hidden",
                          borderRadius: "50%",
                          "&:hover": {
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        {klydo.loopUrl ? (
                          <KlydoLoopUrl loopUrl={klydo.loopUrl} />
                        ) : (
                          <DSAvatar
                            src={klydo.klydoSecureUrl || undefined}
                            sx={{
                              width: "100%",
                              height: "100%",
                            }}
                          >
                            {klydo.name.charAt(0)}
                          </DSAvatar>
                        )}
                      </DSBox>
                    </DSTooltip>
                  ))}
                  {allKlydos.length > 3 && (
                    <DSTypography
                      variant="caption"
                      sx={{
                        fontSize: "0.6rem",
                        color: "text.secondary",
                        alignSelf: "center",
                        cursor: "pointer",
                      }}
                      onClick={handleMoreKlydosClick}
                    >
                      +{allKlydos.length - 3}
                    </DSTypography>
                  )}
                </DSBox>
              )}

              {hasCountry && (
                <DSBox
                  sx={{ mt: 1 }}
                  onClick={(mouseEvent: React.MouseEvent) =>
                    mouseEvent.stopPropagation()
                  }
                >
                  <LoadKlydosButton
                    onLoadKlydos={handleLoadKlydos}
                    klydosLoading={klydosLoading}
                    klydosError={klydosError}
                    klydosLoaded={klydosLoaded}
                  />
                </DSBox>
              )}
            </DSBox>
          )}
        </DSBox>
      </DSCard>

      {showCalendarEventModal && hasHolidayEvents && (
        <MultiCalendarEventModal
          open={showCalendarEventModal}
          onClose={() => setShowCalendarEventModal(false)}
          events={displayedHolidayEvents}
          klydoEvents={klydoEvents}
          date={date}
        />
      )}

      {showKlydoEventDayModal && hasKlydoEvents && (
        <KlydoEventDayModal
          open={showKlydoEventDayModal}
          onClose={() => setShowKlydoEventDayModal(false)}
          date={dateAsJsDate}
          events={klydoEvents}
        />
      )}

      {showCreateKlydoEvent && (
        <UnifiedCalendarEventModal
          open={showCreateKlydoEvent}
          onClose={() => setShowCreateKlydoEvent(false)}
          initialDate={dateAsJsDate}
          minDate={new Date()}
          onKlydoClick={setSelectedKlydo}
        />
      )}

      {selectedKlydo && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={true}
          onClose={() => setSelectedKlydo(null)}
        >
          <KlydoModal
            klydoId={selectedKlydo.id}
            setShowModal={() => setSelectedKlydo(null)}
          />
        </DSDialog>
      )}
    </>
  );
};

export default CalendarEventDay;
